import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Users } from "lucide-react";
import { NewsletterForm } from "../components/NewsletterForm";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  Badge,
} from "../components/ui";
import { useSeo } from "../lib/seo";
import { listPostsSorted } from "../content/blog";

function formatDate(iso: string): string {
  // yyyy-mm-dd → e.g. "May 30, 2026". Locale fixed to en-US so render is deterministic
  // across SSR/CI/browser timezones.
  const parts = iso.split("-").map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const d = parts[2] ?? 1;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function Blog() {
  useSeo({
    title: "Blog — WeConsult Rx",
    description:
      "Product updates, regulatory commentary, and clinical deep-dives from the WeConsult Rx team.",
    path: "/blog",
  });

  const posts = listPostsSorted();

  return (
    <>
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Blog</Eyebrow>
            <Heading level={1} className="mt-3">
              Notes from the consultant pharmacy frontier.
            </Heading>
            <Lede className="mt-6">
              Product updates, regulatory commentary, and clinical deep-dives.
              Substance over hype.
            </Lede>
          </div>

          <ul
            data-testid="blog-post-list"
            className="mt-12 grid gap-6 md:grid-cols-2"
          >
            {posts.map((post) => (
              <Card key={post.slug} as="li" className="flex flex-col">
                <div className="flex flex-wrap gap-2">
                  {post.tags.slice(0, 3).map((t) => (
                    <Badge key={t} tone="forest">
                      {t}
                    </Badge>
                  ))}
                </div>
                <Heading level={3} className="mt-4">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-ink-900 hover:text-forest-700 transition-colors"
                  >
                    {post.title}
                  </Link>
                </Heading>
                <p className="mt-3 text-ink-600 leading-relaxed">
                  {post.description}
                </p>
                <dl className="mt-5 grid grid-cols-1 gap-2 text-sm text-ink-500">
                  <div className="inline-flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-forest-700" aria-hidden />
                    <dt className="sr-only">Published</dt>
                    <dd>{formatDate(post.date)}</dd>
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <Clock className="h-4 w-4 text-forest-700" aria-hidden />
                    <dt className="sr-only">Reading time</dt>
                    <dd>{post.readingTimeMin} min read</dd>
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <Users className="h-4 w-4 text-forest-700" aria-hidden />
                    <dt className="sr-only">Audience</dt>
                    <dd>{post.audience}</dd>
                  </div>
                </dl>
                <div className="mt-6 pt-2 mt-auto">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
                    aria-label={`Read ${post.title}`}
                  >
                    Read article
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </div>
              </Card>
            ))}
          </ul>
        </Container>
      </Section>

      <Section tone="soft" className="!py-16">
        <Container>
          <Card className="max-w-2xl mx-auto bg-ink-900 text-white border-transparent">
            <p className="text-sm uppercase tracking-tagline font-semibold text-forest-400">
              Quarterly newsletter
            </p>
            <Heading level={3} className="mt-3 text-white">
              New posts, in your inbox.
            </Heading>
            <p className="mt-3 text-ink-300">
              Quarterly product updates and the occasional regulatory note. No
              spam. Unsubscribe anytime.
            </p>
            <NewsletterForm className="mt-5" />
          </Card>
        </Container>
      </Section>
    </>
  );
}
